import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { PageLoadingSpinner } from "../components/PageLoadingSpinner";
import {
  AUTH_PATHS,
  AuthMachineStatus,
  BAA_PATH,
  DEFAULT_PROTECTED_ROUTE_PATH,
  SUBSCRIPTION_PATH,
} from "../constants";
import { useAuth } from "../contexts/AuthContext";
import { parseAuthLinkError } from "../hooks/auth/authLinkError";
import { parseAuthLinkToken } from "../hooks/auth/authLinkToken";
import { useAuthV2 } from "../hooks/auth/useAuthV2";
import { useBAAStatusData } from "../hooks/baa/useBAAStatusData";
import { useAuthRedirect } from "../hooks/useAuthRedirect";
import { useSubscriptionStatus } from "../hooks/useSubscriptionStatus";
import { capturePreAuthRedirect } from "../utils";

export const ProtectedRoute: React.FC = () => {
  const location = useLocation();
  const { user, isFreshSignIn, markSignInComplete, firmContext } = useAuth();
  const { isPasswordResetPending, authStatus } = useAuthV2();
  const { redirectToAuth } = useAuthRedirect();
  const { baaStatus, isLoading: isBAAChecking } = useBAAStatusData();
  const { hasSubscription, isLoading: isSubscriptionChecking } = useSubscriptionStatus();

  useEffect(() => {
    if (authStatus === AuthMachineStatus.EXPIRED) {
      redirectToAuth({
        errorMessage: "Your session has expired. Please sign in again.",
      });
    }
  }, [authStatus, redirectToAuth]);

  if (!user) {
    if (location.pathname === AUTH_PATHS.UPDATE && parseAuthLinkToken(location) !== null) {
      return <Outlet />;
    }

    const linkError = parseAuthLinkError(location.hash);
    const options = capturePreAuthRedirect({
      location,
      extraState: linkError ? { errorMessage: linkError } : {},
    });
    return <Navigate to={AUTH_PATHS.SIGNIN} {...options} />;
  }

  // Wait for firmContext to load before proceeding with any checks
  if (!firmContext) {
    return <PageLoadingSpinner />;
  }

  /*
  Checking priority:
  1. Password reset required
  2. BAA status needed
  3. Subscription check (only if going to cases page or subscription page)
  */
  if (isPasswordResetPending) {
    if (location.pathname !== AUTH_PATHS.UPDATE) {
      const options = capturePreAuthRedirect({ location });
      return <Navigate to={AUTH_PATHS.UPDATE} {...options} />;
    }
  } else {
    if (isBAAChecking) {
      return <PageLoadingSpinner />;
    } else if (baaStatus?.needs_signature && location.pathname !== BAA_PATH) {
      const options = capturePreAuthRedirect({ location });
      return <Navigate to={BAA_PATH} {...options} />;
    } else {
      if (
        isFreshSignIn &&
        (location.pathname === DEFAULT_PROTECTED_ROUTE_PATH ||
          location.pathname === SUBSCRIPTION_PATH)
      ) {
        if (isSubscriptionChecking) {
          return <PageLoadingSpinner />;
        }
        markSignInComplete();
        if (!hasSubscription) {
          return <Navigate to={SUBSCRIPTION_PATH} />;
        }
      }
    }
  }

  return <Outlet />;
};
